import {
  Archive,
  ArchiveRestore,
  Bell,
  BellOff,
  CheckCheck,
  Clock,
  Infinity as Always,
  MessageCircleDashed,
  Moon,
  Pin,
  PinOff,
} from 'lucide-react';
import { ChatSheet, type ChatSheetRow } from './ChatSheet';
import { hasUnread } from '../../lib/chatSettings';
import { appLocale } from '../../lib/format';
import { useT } from '../../i18n';
import type { ChatConversation } from './ConversationDialogs';

export type MuteChoice = '8h' | '1w' | 'always';

/** "Tue 18:00", for "Muted until …" (D233). */
export function muteUntilLabel(iso: string): string {
  return new Date(iso).toLocaleString(appLocale(), {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface ChatActionsSheetProps {
  conversation: ChatConversation | null;
  name: string;
  muted: boolean;
  mutedUntil: string | null;
  onClose: () => void;
  onMarkUnread: (unread: boolean) => void;
  onMute: () => void;
  onUnmute: () => void;
  onArchive: (archived: boolean) => void;
  onKeepArchived: (keep: boolean) => void;
  /** Pin or unpin (D237). */
  onPin: (pinned: boolean) => void;
}

// ⋯ or press and hold on a home tile (M63): read/unread (D232), mute
// (D233), archive and "Keep archived" (D231).
export function ChatActionsSheet({
  conversation,
  name,
  muted,
  mutedUntil,
  onClose,
  onMarkUnread,
  onMute,
  onUnmute,
  onArchive,
  onKeepArchived,
  onPin,
}: ChatActionsSheetProps) {
  const t = useT();
  if (!conversation) return null;
  const archived = Boolean(conversation.archivedAt);
  const unread = hasUnread(conversation);
  const rows: ChatSheetRow[] = [
    unread
      ? {
          key: 'read',
          label: t('chat.chatActions.markRead'),
          icon: <CheckCheck size={20} />,
          onSelect: () => onMarkUnread(false),
        }
      : {
          key: 'unread',
          label: t('chat.chatActions.markUnread'),
          icon: <MessageCircleDashed size={20} />,
          onSelect: () => onMarkUnread(true),
        },
    muted
      ? {
          key: 'unmute',
          label: t('chat.chatActions.unmute'),
          icon: <Bell size={20} />,
          detail: mutedUntil
            ? t('chat.chatActions.mutedUntil', {
                time: muteUntilLabel(mutedUntil),
              })
            : t('chat.chatActions.mutedAlways'),
          onSelect: onUnmute,
        }
      : {
          key: 'mute',
          label: t('chat.chatActions.mute'),
          icon: <BellOff size={20} />,
          onSelect: onMute,
        },
  ];
  // "Everyone" stays in the list (D231) and is always first (D237).
  if (!conversation.isDefault) {
    rows.unshift(
      conversation.pinnedAt
        ? {
            key: 'unpin',
            label: t('chat.chatActions.unpin'),
            icon: <PinOff size={20} />,
            onSelect: () => onPin(false),
          }
        : {
            key: 'pin',
            label: t('chat.chatActions.pin'),
            icon: <Pin size={20} />,
            onSelect: () => onPin(true),
          }
    );
    rows.push(
      archived
        ? {
            key: 'unarchive',
            label: t('chat.chatActions.unarchive'),
            icon: <ArchiveRestore size={20} />,
            onSelect: () => onArchive(false),
          }
        : {
            key: 'archive',
            label: t('chat.chatActions.archive'),
            icon: <Archive size={20} />,
            onSelect: () => onArchive(true),
          }
    );
    if (archived) {
      rows.push({
        key: 'keep',
        label: t('chat.chatActions.keepArchived'),
        icon: <Archive size={20} />,
        toggle: {
          checked: Boolean(conversation.keepArchived),
          hint: t('chat.chatActions.keepArchivedHint'),
        },
        onSelect: () => onKeepArchived(!conversation.keepArchived),
      });
    }
  }
  return (
    <ChatSheet
      isOpen
      title={name}
      rows={rows}
      label={t('chat.chatActions.label')}
      onClose={onClose}
    />
  );
}

// Mute for 8 hours, a week or always (D233).
export function MuteSheet({
  name,
  isOpen,
  onPick,
  onClose,
}: {
  name: string;
  isOpen: boolean;
  onPick: (choice: MuteChoice) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <ChatSheet
      isOpen={isOpen}
      title={t('chat.mute.title', { name })}
      body={t('chat.mute.body')}
      label={t('chat.mute.title', { name })}
      onClose={onClose}
      rows={[
        {
          key: '8h',
          label: t('chat.mute.eightHours'),
          icon: <Moon size={20} />,
          onSelect: () => onPick('8h'),
        },
        {
          key: '1w',
          label: t('chat.mute.oneWeek'),
          icon: <Clock size={20} />,
          onSelect: () => onPick('1w'),
        },
        {
          key: 'always',
          label: t('chat.mute.always'),
          icon: <Always size={20} />,
          onSelect: () => onPick('always'),
        },
      ]}
    />
  );
}
